import React, { useState, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

// Helper to optimize Cloudinary URLs for thumbnails
const getOptimizedImageUrl = (url, width = 400) => {
  if (!url) return url;
  // If it's a Cloudinary URL, add transformations
  if (url.includes('cloudinary.com') && url.includes('/upload/')) {
    return url.replace('/upload/', `/upload/w_${width},h_${width},c_fill,q_auto,f_auto/`);
  }
  return url;
};

const ProductCard = memo(({ product, onAddToCart }) => {
  const navigate = useNavigate();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Support both old (scentsAvailable) and new (scentsWithImages) structures
  const scentsAvailable = product.scentsWithImages 
    ? product.scentsWithImages.map(s => s.scent)
    : (product.scentsAvailable || [product.scent]);
  
  const [selectedScent, setSelectedScent] = useState(scentsAvailable[0] || product.scent);

  // Get images for selected scent
  const getImagesForScent = (scent) => {
    if (product.scentsWithImages) {
      const scentObj = product.scentsWithImages.find(s => s.scent === scent);
      return scentObj?.images || [];
    }
    return product.images || [product.image];
  };

  const currentImages = getImagesForScent(selectedScent);
  const mainImage = currentImages[0] || product.image;
  // Use optimized thumbnail for product cards
  const thumbnailImage = getOptimizedImageUrl(mainImage, 400);

  const handleAddToCart = (e) => {
    e.stopPropagation();
    const productWithScent = {
      ...product,
      selectedScent: selectedScent,
      displayName: `${product.name} - ${selectedScent}`,
      image: mainImage,
      images: currentImages
    };
    onAddToCart(productWithScent);
  };

  return (
    <article 
      className="group cursor-pointer bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex flex-col h-full"
      itemScope
      itemType="https://schema.org/Product"
      data-testid={`product-card-${product.id}`}
    >
      {/* Image Container - uses object-contain to show full image */}
      <div 
        className="relative overflow-hidden bg-gray-100" 
        style={{ paddingBottom: '100%' }}
        onClick={() => navigate(`/product/${product.id}`)}
      >
        {/* Placeholder skeleton while image loads */}
        {!imageLoaded && !imageError && (
          <div className="absolute inset-0 bg-gray-200 animate-pulse" />
        )}
        <img
          src={imageError ? 'https://via.placeholder.com/400x400?text=No+Image' : thumbnailImage}
          alt={`${product.name} - Handmade ${product.category || 'gift'} UK | Buy artisan ${product.name} online at Adina's Crafts`}
          title={`${product.name} - Handmade in UK | £${product.price} | Free delivery over £50`}
          loading="lazy"
          decoding="async"
          itemProp="image"
          className={`absolute inset-0 w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImageLoaded(true)}
          onError={() => {
            setImageError(true);
            setImageLoaded(true);
          }}
        />
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2 z-10">
          {product.badges && product.badges.includes('New') && (
            <span className="bg-amber-400 text-black px-3 py-1 rounded-full text-xs font-semibold shadow-md">
              New
            </span>
          )}
          {product.badges && product.badges.includes('Bestseller') && (
            <span className="bg-black text-white px-3 py-1 rounded-full text-xs font-semibold shadow-md">
              Bestseller
            </span>
          )}
        </div>
      </div>

      {/* Content - flex-grow to push button to bottom */}
      <div className="p-4 flex flex-col flex-grow">
        <h3 
          className="text-lg font-serif font-semibold text-gray-800 mb-2 line-clamp-2 hover:text-amber-600 transition-colors min-h-[3.5rem]"
          onClick={() => navigate(`/product/${product.id}`)}
          itemProp="name"
        >
          {product.name}
        </h3>
        
        {/* Hidden SEO description */}
        <meta itemProp="description" content={product.description || product.name} />

        {/* Scent Selector */}
        <div className="mb-3 min-h-[2.5rem]">
          {scentsAvailable && scentsAvailable.length > 1 ? (
            <div>
              <label className="text-xs text-gray-600 mb-1 block font-semibold">
                Choose Scent:
              </label>
              <Select value={selectedScent} onValueChange={setSelectedScent}>
                <SelectTrigger className="w-full h-9 text-sm bg-gray-50 border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {scentsAvailable.map((scent) => (
                    <SelectItem key={scent} value={scent} className="text-sm">
                      {scent}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <p className="text-xs font-semibold text-gray-600 tracking-wider">
              Scent: {product.scent || 'Classic'}
            </p>
          )}
        </div>

        {/* Price and Button - pushed to bottom with mt-auto */}
        <div className="mt-auto" itemProp="offers" itemScope itemType="https://schema.org/Offer">
          <meta itemProp="priceCurrency" content="GBP" />
          <meta itemProp="availability" content={product.inStock !== false ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"} />
          <p className="text-2xl font-bold text-amber-600 mb-3" itemProp="price" content={product.price}>
            £{product.price?.toFixed(2) || '0.00'}
          </p>
          <Button
            onClick={handleAddToCart}
            className="w-full bg-amber-400 hover:bg-amber-500 text-black font-semibold transition-colors"
            data-testid={`add-to-cart-${product.id}`}
          >
            Add to Cart
          </Button>
        </div>
      </div>
    </article>
  );
});

ProductCard.displayName = 'ProductCard';

export default ProductCard;
